import { DataCloudEventData } from '~/modules/services/analyticsService';
import { randomUUID } from '~/modules/utilities/cross_env_utils';
import { AnalyticsEventTopic } from '../analyticsHelper';
import { AI_OVERVIEW_SEARCH_ELEMENT_TYPE } from '../gtmEventTypes/aiSearchConstants';
import { AI_OVERVIEW_EVENT_NAME, DataCloudEventTypeValues } from './constants';
import { DataCloudAnalyticsEvent, DataCloudEventType, DataCloudIdentityData } from './datacloudEventType';

export const clamp = (n: number, lo: number, hi: number): number => Math.min(hi, Math.max(lo, n));
export const isFiniteNumber = (n: unknown): n is number => typeof n === 'number' && Number.isFinite(n);

// Required Text fields are dropped by Data Cloud ingestion when empty, so a
// missing value is sent as the sentinel 'none' rather than an empty string.
const REQUIRED_STRING_FALLBACK = 'none';

const requiredString = (raw: string | undefined): string => {
    const value = (raw ?? '').trim();
    return value || REQUIRED_STRING_FALLBACK;
};

const normalizeAppliedString = (raw: string | undefined): string => {
    const value = (raw ?? '')
        .replace(/[\r\n]+/g, ' ')
        .trim()
        .slice(0, 500);
    return value || REQUIRED_STRING_FALLBACK;
};

export interface AiOverviewEventInteraction {
    name: string;
    eventType: string;
    deviceId: string;
    anonymous: string;
    language?: string;
    userAgent?: string;
    searchString?: string;
    searchQueryUid: string;
    'co-relation-id': string;
    aiOverviewStatus: string;
    sortingApplied?: string;
    filterApplied?: string;
    source?: string;
    aiOverviewListingCount?: number;
    aiOverviewLatencyMs?: number;
    aiOverviewExpandSource?: string;
    aiOverviewWasRefreshed?: string;
    aiOverviewErrorToken?: string;
    message?: string;
    TokenCount?: number;
    TTFT?: number;
}

export interface AiOverviewEventDataCloudAnalyticsEvent {
    interaction: AiOverviewEventInteraction;
    user: {
        attributes: DataCloudIdentityData & {
            eventType: string;
            deviceId: string;
        };
    };
}

/**
 * `co-relation-id` is the per-summarize id shared by every row of one result
 * set, so Data Cloud can stitch them together; the UUID is only a fallback
 * for the unexpected case where an impression fires before the id is set.
 */
export abstract class AiOverviewDataCloudEventType extends DataCloudEventType {
    protected buildEnvelope(
        event: DataCloudEventData,
        baseEvent: DataCloudAnalyticsEvent,
        eventType: DataCloudEventTypeValues,
    ): AiOverviewEventInteraction {
        const interaction: AiOverviewEventInteraction = {
            name: AI_OVERVIEW_EVENT_NAME,
            eventType,
            deviceId: baseEvent.username,
            anonymous: baseEvent.anonymous ? '1' : '0',
            language: baseEvent.language,
            userAgent: baseEvent.userAgent,
            searchQueryUid: requiredString(event.searchQueryUid),
            'co-relation-id': event.aiOverviewCorrelationId?.trim() || randomUUID(),
            aiOverviewStatus: event.aiOverviewStatus ?? 'idle',
            // Required Text fields on the shared object, so all three events emit them.
            filterApplied: normalizeAppliedString(event.filterApplied),
            sortingApplied: normalizeAppliedString(event.sortingApplied),
        };
        const searchString = (event.keyword ?? '').trim();
        if (searchString) {
            interaction.searchString = searchString;
        }
        return interaction;
    }

    protected buildUser(baseEvent: DataCloudAnalyticsEvent): AiOverviewEventDataCloudAnalyticsEvent['user'] {
        return {
            attributes: {
                ...baseEvent.userData,
                eventType: DataCloudEventTypeValues.identity,
                deviceId: baseEvent.username,
            },
        };
    }
}

export class AiOverviewImpressionDataCloudEventType extends AiOverviewDataCloudEventType {
    canHandle(event: DataCloudEventData): boolean {
        return (
            event.topic === AnalyticsEventTopic.ANALYTICS_SHOWN_IMPRESSION &&
            event.elementType === AI_OVERVIEW_SEARCH_ELEMENT_TYPE
        );
    }

    getBody(event: DataCloudEventData): AiOverviewEventDataCloudAnalyticsEvent {
        const baseEvent = this.baseBody(event);
        const interaction = this.buildEnvelope(event, baseEvent, DataCloudEventTypeValues.impression);
        interaction.aiOverviewListingCount = isFiniteNumber(event.aiOverviewListingCount)
            ? Math.max(0, event.aiOverviewListingCount)
            : 0;
        interaction.aiOverviewLatencyMs = isFiniteNumber(event.aiOverviewLatencyMs)
            ? clamp(event.aiOverviewLatencyMs, 0, 120000)
            : 0;
        // Spec defines TokenCount as 0 for the impression action.
        interaction.TokenCount = isFiniteNumber(event.aiOverviewTokenCount)
            ? clamp(event.aiOverviewTokenCount, 0, 100000)
            : 0;
        if (isFiniteNumber(event.aiOverviewTtft)) {
            interaction.TTFT = clamp(event.aiOverviewTtft, 0, 60000);
        }
        const source = event.source?.trim();
        if (source) {
            interaction.source = source;
        }
        return {
            interaction,
            user: this.buildUser(baseEvent),
        };
    }
}
